import Controller, { OnBeforeSubmit, OnCancel, OnChange, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../BaseController';
import { Inject } from '@odinjs/odin';
import { FragmentNames, RespiratoryMonitoringEditorAliases } from '../../../codegen';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { AbortableSubmitEvent, ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ActionAPI, AutocompleteAPI, ButtonAPI, FetchAPI, FormAPI, InputAPI, ToastService } from '@philips-internal/oswald-ui-components';
import moment from 'moment';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import ShowToast from '../../../utils/toast-service';
import { Endpoint } from '../../../constants/endpoints';
import { Value } from '@philips-internal/oswald-ui-core';
import { SimpleDialog } from '../../dialog-helper';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { TimeUtils } from '../../../utils/time-utils';
import { DialogType, ModalType, SimpleDialogParams } from '../../../interfaces/simple-dialog';

interface FormValues {
  accessory?: AccessoryProps;
  amp?: number;
  breath?: BreathProps;
  equipment?: EquipmentProps
  fio?: string;
  fluxoInsp?: string;
  freq?: string;
  insp?: string;
  modality?: ModalityProps;
  musculature?: MusculatureProps;
  nitric?: string;
  oxide?: string;
  oxygen?: string;
  peep?: string;
  pip?: string;
  pmva?: string;
  recordDate?: string
  sat?: string;
  standard?: StandardProps;
  vent?: string;
}

interface StandardProps {
  standardDescription: string;
  standardValue: number;
}

interface ModalityProps {
  modalityDescription: string;
  modalityValue: string;
}
interface MusculatureProps {
  musculatureDescription: string;
  musculatureValue: number;
}

interface BreathProps {
  breathDescription: string;
  breathValue: string;
}

interface EquipmentProps {
  equipmentDescription: string;
  equipmentValue: number;
}

interface AccessoryProps {
  accessoryDescription: string;
  accessoryValue: string;
}

enum BreathTypes {
  VMNI = 'VMNI',
  ESPONT = 'ESPONT',
  ABLE = 'S',
  REQUIRED = 'O'
}

@Controller({ fragment: FragmentNames.RESPIRATORY_MONITORING_EDITOR })
class RespiratoryMonitoringEditorController extends BaseController {

  internalNavigation!: boolean;

  @Inject(RespiratoryMonitoringEditorAliases.RESPIRATORY_MONITORING_EDITOR_FORM)
  private respiratoryMonitoringFormApi!: FormAPI;

  @Inject(RespiratoryMonitoringEditorAliases.AUTOCOMPLETE_STANDARD)
  private standardAutoCompleteApi!: AutocompleteAPI;

  @Inject(RespiratoryMonitoringEditorAliases.AUTOCOMPLETE_ACCESSORY)
  private accessoryAutoCompleteApi!: AutocompleteAPI;

  @Inject(RespiratoryMonitoringEditorAliases.AUTOCOMPLETE_MODALITY)
  private modalityAutoCompleteApi!: AutocompleteAPI;

  @Inject(RespiratoryMonitoringEditorAliases.FETCH_MODALITY)
  private modalityFetchApi!: FetchAPI;

  @Inject(RespiratoryMonitoringEditorAliases.AUTOCOMPLETE_EQUIPMENT)
  private equipmentAutoCompleteApi!: AutocompleteAPI;

  @Inject(RespiratoryMonitoringEditorAliases.RELEASE_BUTTON)
  private buttonReleaseApi!: ButtonAPI;

  @Inject(RespiratoryMonitoringEditorAliases.ACTION_DELETE_RESPIRATORY_MONITORING)
  private deleteActionApi!: ActionAPI;

  @Inject(RespiratoryMonitoringEditorAliases.INPUT_DATETIME)
  private recordDateInputApi!: InputAPI;

  discard(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  release(patientId: string, encounterId: number, sequenceNumber: number): void {
    HttpService.put(Endpoint.RESPIRATORY_MONITORING_RELEASE, { encounterId, patientId, sequenceNumber }).then(response => {
      if (response.status === 200 && response.body) {
        applicationContext.popNavigationItem();
      }
    }).catch(error => {
      this.buttonReleaseApi.setVisible(true);
      this.respiratoryMonitoringFormApi.setEditing(true);
      this.respiratoryMonitoringFormApi.setDisabled(true);
      ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
    })
  }

  confirmCancelAndRelease(patientId: string, encounterId: number, sequenceNumber: number): void {
    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'patient-evolution.release-title-evolution',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.discard() },
      onClickOk: async () => { this.release(patientId, encounterId, sequenceNumber) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  insertRespiratoryMonitoring(body: Record<string, Value>, event: AbortableSubmitEvent): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.RESPIRATORY_MONITORING_PERSIST, { encounterId, patientId }, { body }).then(response => {

      if(response.body) {
        const responseBody = response.body as Record<string, Value>;
        const { sequenceNumber } = responseBody;
        if(sequenceNumber) {
          this.respiratoryMonitoringFormApi.setEditing(false);
          this.respiratoryMonitoringFormApi.setDisabled(true);
          this.confirmCancelAndRelease(String(patientId), Number(encounterId), Number(sequenceNumber));
        }
      }
    }).catch(error => {
      event.abort();
      this.respiratoryMonitoringFormApi.setEditing(true);
      this.respiratoryMonitoringFormApi.setDisabled(false);
      ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
    });
  }

  invalidDate(): boolean {
    const recordDate = this.context.get('recordDate') as moment.Moment;
    const newDate = moment(recordDate);
    const today = moment();
    if (newDate.diff(today, 'days') > 0) {
      ToastService.open({
        dismissExpression: MessageService.translate('warning.clinical-note.later-date'),
        dismissible: false,
        highlight: false,
        id: 'administration-by-process',
        level: ToastLevel.WARNING,
        message: MessageService.translate('warning.clinical-note.later-date'),
        placement: Placement.BOTTOM,
        showIcon: true,
        timeout: 5000,
        type: ToastType.BANNER,
      });
      return true;
    }
    return false;
  }

  delete(patientId: string, encounterId: number, sequenceNumber: number): void {
    HttpService.delete(Endpoint.RESPIRATORY_MONITORING_DELETE, { encounterId, patientId, sequenceNumber }).then(response => {
      if (response.status === 200 && response.body) {
        applicationContext.popNavigationItem();
      }
    })
  }

  cancelDiscard(): void {
    this.respiratoryMonitoringFormApi.setDisabled(false);
    this.respiratoryMonitoringFormApi.setData({
      editing: true,
      innerEditing: true,
    });
  }

  cancel(): void {
    const dialogCancelParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'clinical-notes.discard-changes',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.cancelDiscard() },
      onClickOk: () => { this.discard() },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit',
    }

    SimpleDialog.openDialog(dialogCancelParams);
  }

  creationRespiratoryMonitoring(): void {
    const today     = moment();
    const emptyChar = '';

    this.respiratoryMonitoringFormApi.setData({ recordDate: today.format('L LTS') });

    TimeUtils.setDateToInput(this.recordDateInputApi)
      .then((date) => {
        this.context.set({recordDate: date });
      })

    this.handleConditionalFields(emptyChar);
  }

  editRespiratoryMonitoring(sequenceNumber: number): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    HttpService.get(Endpoint.RESPIRATORY_MONITORING_EDIT, {
      encounterId,
      patientId,
      sequenceNumber
    }
    ).then(response => {
      const record = response.body as Record<string, Value>;
      this.context.set({recordDate: moment(String(record.recordDate)) });

      const currentRecord = this.respiratoryMonitoringFormApi.getRecord();

      this.respiratoryMonitoringFormApi.setRecord({
        ...currentRecord,
        accessory: {
          accessoryDescription: record.dsDisp,
          accessoryValue: record.ieDispRespEsp,
        },
        amp: record.qtAmplitude,
        breath: {
          breathDescription: record.dsRespiracao,
          breathValue: record.ieRespiracao,
        },
        equipment: {
          equipmentDescription: record.dsEquipamento,
          equipmentValue: record.nrSeqEquipamento,
        },
        fio: record.qtFio,
        fluxoInsp: record.qtFluxoInsp,
        freq: record.qtFreqProg,
        insp: record.qtTempoInsp,
        modality: {
          modalityDescription: record.dsModVent,
          modalityValue: record.cdModVent,
        },
        musculature: {
          musculatureDescription: record.dsMusculo,
          musculatureValue: record.ieMusculo,
        },
        nitric: record.qtOxidoNitrico,
        oxide: record.qtFluxoOxido,
        oxygen: record.qtFluxoOxigenio,
        peep: record.qtPeep,
        pip: record.qtPip,
        pmva: record.qtPva,
        recordDate: record.recordDate,
        sat: record.qtSaturacao,
        standard: {
          standardDescription: record.dsPadrao,
          standardValue: record.iePadraoResp,
        },
        vent: record.qtFreqVent,
      });

      this.handleConditionalFields(String(record.ieRespiracao));
    });
  }

  handleConditionalFields(breathValue: string): void {
    const isEspont     = breathValue === BreathTypes.ESPONT;
    const isVmni       = breathValue === BreathTypes.VMNI;
    const isEspontVmni = isEspont || isVmni;

    this.standardAutoCompleteApi.setEnabled(isEspontVmni);
    this.accessoryAutoCompleteApi.setEnabled(isEspontVmni);
    this.modalityAutoCompleteApi.setEnabled(!isEspont);
    this.equipmentAutoCompleteApi.setEnabled(!isEspont);
    this.modalityFetchApi.setParameters({ breathValue });
  }

  handleConditionalFieldsValue(): void {
    this.standardAutoCompleteApi.setValue(undefined);
    this.accessoryAutoCompleteApi.setValue(undefined);
    this.modalityAutoCompleteApi.setValue(undefined);
    this.equipmentAutoCompleteApi.setValue(undefined);
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.RESPIRATORY_MONITORING_LAYOUT })
  onMount(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    this.context.set({encounterId, patientId});
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.RELEASE_BUTTON })
  onReleaseButtonMount(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();
    if(sequenceNumber) {
      this.respiratoryMonitoringFormApi.setDisabled(true);
      this.buttonReleaseApi.setVisible(true);
    } else {
      this.respiratoryMonitoringFormApi.setDisabled(false);
      this.buttonReleaseApi.setVisible(false);
    }
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.ACTION_DELETE_RESPIRATORY_MONITORING })
  onDeleteMount(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();
    if(sequenceNumber) {
      this.deleteActionApi.setEnabled(true);
    }
  }

  @OnMount({target: RespiratoryMonitoringEditorAliases.RESPIRATORY_MONITORING_EDITOR_FORM})
  onFormMount(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();
    if(sequenceNumber) {
      this.editRespiratoryMonitoring(Number(sequenceNumber));
      return;
    }
    this.creationRespiratoryMonitoring();
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.FETCH_ACCESSORY })
  onFetchAccessoryMount(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    fetch.setParameters({}, { skip: true });
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.FETCH_BREATH })
  onFetchBreathMount(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    fetch.setParameters({}, { skip: true });
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.FETCH_EQUIPMENT })
  onFetchEquipmentMount(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    fetch.setParameters({}, { skip: true });
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.FETCH_MODALITY })
  onFetchModalityMount(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    fetch.setParameters({}, { skip: true });
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.FETCH_MUSCULATURE })
  onFetchMusculatureMount(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    fetch.setParameters({}, { skip: true });
  }

  @OnMount({ target: RespiratoryMonitoringEditorAliases.FETCH_STANDARD })
  onFetchStandardMount(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    fetch.setParameters({}, { skip: true });
  }

  @OnClick({ target: RespiratoryMonitoringEditorAliases.RELEASE_BUTTON })
  onReleaseClick(): void {
    const { encounterId, patientId, sequenceNumber } = this.historyService.getCurrentRouteParams();
    if (encounterId && patientId && sequenceNumber) {
      this.buttonReleaseApi.setVisible(false);
      this.respiratoryMonitoringFormApi.setEditing(false);
      this.respiratoryMonitoringFormApi.setDisabled(true);
      this.release(String(patientId), Number(encounterId), Number(sequenceNumber));
    }
  }

  @OnClick({ target: RespiratoryMonitoringEditorAliases.ACTION_DELETE_RESPIRATORY_MONITORING })
  onDelete(): void {
    const { patientId, encounterId, sequenceNumber } = this.historyService.getCurrentRouteParams();

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { applicationContext.popNavigationItem(); },
      onClickOk: async () => { this.delete(String(patientId), Number(encounterId), Number(sequenceNumber)) },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'patient-evolution.delete-notnow-evolution',
      titleOnOk: 'patient-evolution.delete-evolution',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  @OnChange({ target: RespiratoryMonitoringEditorAliases.RESPIRATORY_MONITORING_EDITOR_FORM })
  onRespiratoryMonitoringChanges(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();

    if (sequenceNumber) {
      this.respiratoryMonitoringFormApi.setDisabled(false);
    }
    this.respiratoryMonitoringFormApi.validate();
  }

  @OnChange({ target: RespiratoryMonitoringEditorAliases.INPUT_DATETIME })
  onDataTimeInputChange(event: ChangeEvent<InputAPI>): void {
    const newDateState = moment(String(event.value));
    this.context.set({recordDate: newDateState});
  }

  @OnChange({ target: RespiratoryMonitoringEditorAliases.FIELD_BREATH })
  onBlur(event: ChangeEvent<BreathProps>): void {
    const breath      = event.value;
    const emptyChar   = '';
    const breathValue = breath ? breath.breathValue : emptyChar;

    this.handleConditionalFields(breathValue);
    this.handleConditionalFieldsValue();
  }

  @OnCancel({target: RespiratoryMonitoringEditorAliases.RESPIRATORY_MONITORING_EDITOR_FORM})
  onFormBeforeCancel(): void {
    const record = this.respiratoryMonitoringFormApi.getRecord();
    this.respiratoryMonitoringFormApi.setRecord(record);
    this.respiratoryMonitoringFormApi.setDisabled(true);
    this.cancel();
  }

  @OnBeforeSubmit({target: RespiratoryMonitoringEditorAliases.RESPIRATORY_MONITORING_EDITOR_FORM})
  onSubmitForm(event: AbortableSubmitEvent): void {
    const { encounterId, patientId, sequenceNumber } = this.historyService.getCurrentRouteParams();

    const formData = this.respiratoryMonitoringFormApi.getRecord();

    if(this.invalidDate()) {
      event.abort();
      return;
    }

    const recordDate = moment(formData.recordDate as string).utc().format(ISO_FULL_DATE_FORMAT);
    const formValues = event.record as FormValues;

    const body = {
      cdModVent: formValues.modality?.modalityValue,
      encounterId,
      ieDispRespEsp: formValues?.accessory?.accessoryValue,
      ieMusculo: formValues?.musculature?.musculatureValue,
      iePadraoResp: formValues?.standard?.standardValue,
      ieRespiracao: formValues?.breath?.breathValue,
      nrSeqEquipamento: formValues?.equipment?.equipmentValue,
      patientId,
      qtAmplitude: formValues.amp,
      qtFio: formValues.fio,
      qtFluxoInsp: formValues.fluxoInsp,
      qtFluxoOxido: formValues.oxide,
      qtFluxoOxigenio: formValues.oxygen,
      qtFreqProg: formValues.freq,
      qtFreqVent: formValues.vent,
      qtOxidoNitrico: formValues.nitric,
      qtPeep: formValues.peep,
      qtPip: formValues.pip,
      qtPva: formValues.pmva,
      qtSaturacao: formValues.sat,
      qtTempoInsp: formValues.insp,
      recordDate,
      sequenceNumber
    } as Record<string, Value>;

    this.insertRespiratoryMonitoring(body, event);
  }

}

export default RespiratoryMonitoringEditorController;
